import amazonAds from "./amazonAds.js";
import { enableDebug as enableDebug$1, setConsent, hasConsent as hasConsent$1, registerProvider } from "./consent.js";
import googleTags from "./google.js";
import { getConfiguredStates, getLDUOptions, shouldApplyLDU, isAffectedState, configureLDUStates } from "./limitedDataUse.js";
import metaPixel from "./metaPixel.js";
import tddPixel from "./tdd.js";
import { parseEventAndPayload } from "./utils.js";
const DEBUG_KEY = "bc-debug";
const DEBUG_STORAGE_KEY = "bc-debug";
const OVERRIDE_CONSENT_KEY = "bc_tracking_consent";
let debug = false;
const log = (...args) => {
  if (!debug) return;
  console.info(...["[externalTracking]"].concat(args));
};
const trackingProviders = {
  Meta: {
    enableDebug: () => metaPixel.enableDebug(),
    track: ({ brandId, pixels, eventName, eventType, eventPayload, trackingOptions, externalTracking }) => metaPixel.track({ brandId, pixels, eventName, eventType, eventPayload, trackingOptions, externalTracking }),
    grantConsent: () => metaPixel.grantConsent(),
    revokeConsent: () => metaPixel.revokeConsent()
  },
  GoogleAds: {
    enableDebug: () => googleTags.enableDebug(),
    track: ({ brandId, pixels, eventName, eventType, eventPayload }) => googleTags.track({ brandId, pixels, eventName, eventType, eventPayload }),
    grantConsent: () => googleTags.grantConsent(),
    revokeConsent: () => googleTags.revokeConsent()
  },
  TTD: {
    enableDebug: () => tddPixel.enableDebug(),
    track: ({ brandId, pixels, eventName, eventType, eventPayload }) => tddPixel.track({ brandId, pixels, eventName, eventType, eventPayload }),
    grantConsent: () => tddPixel.grantConsent(),
    revokeConsent: () => tddPixel.revokeConsent()
  },
  AmazonAds: {
    enableDebug: () => amazonAds.enableDebug(),
    track: ({ brandId, pixels, eventName, eventType, eventPayload }) => amazonAds.track({ brandId, pixels, eventName, eventType, eventPayload }),
    grantConsent: () => amazonAds.grantConsent(),
    revokeConsent: () => amazonAds.revokeConsent()
  }
};
Object.values(trackingProviders).forEach((provider) => {
  registerProvider(provider);
});
function registerTrackingProvider(providerId, provider) {
  trackingProviders[providerId] = provider;
  registerProvider(provider);
  log(`Registered tracking provider: ${providerId}`);
}
function enableDebug() {
  if (debug) return;
  debug = true;
  try {
    localStorage == null ? void 0 : localStorage.setItem(DEBUG_STORAGE_KEY, "1");
  } catch (e) {
  }
  log("Debug mode enabled");
  Object.values(trackingProviders).forEach((provider) => provider.enableDebug());
  enableDebug$1();
  if (typeof window !== "undefined") {
    window.__bcDebug = {
      // LDU testing helpers
      ldu: {
        getConfiguredStates,
        isAffectedState: (state) => isAffectedState(state),
        shouldApplyLDU: (shipping, billing) => shouldApplyLDU(shipping, billing),
        getLDUOptions: (shipping, billing) => getLDUOptions(shipping, billing),
        configureStates: (states) => configureLDUStates(states)
      },
      // Quick test helper for LDU
      testLDU: (shippingState, billingState) => {
        const result = {
          configuredStates: getConfiguredStates(),
          shippingState,
          billingState,
          shippingAffected: isAffectedState(shippingState),
          billingAffected: isAffectedState(billingState),
          shouldApply: shouldApplyLDU(shippingState, billingState),
          options: getLDUOptions(shippingState, billingState)
        };
        console.table(result);
        return result;
      },
      // Dry-run mode: logs checkout & analytics but doesn't place orders or send pixels
      // Auto-expires after 10 minutes by default
      dryRun: {
        enable: (minutes = 10) => {
          const expiresAt = Date.now() + minutes * 60 * 1e3;
          localStorage.setItem("bc-do-not-place-order", String(expiresAt));
          console.log(`🧪 DRY RUN mode ENABLED - expires in ${minutes} minutes (${new Date(expiresAt).toLocaleTimeString()})`);
          console.log("   • Checkout: orders will NOT be placed");
          console.log("   • Analytics: events will be logged but NOT sent to pixels");
        },
        disable: () => {
          localStorage.removeItem("bc-do-not-place-order");
          console.log("✅ DRY RUN mode DISABLED - orders will be placed, analytics will be sent");
        },
        isEnabled: () => {
          const expiresAt = localStorage.getItem("bc-do-not-place-order");
          if (!expiresAt) return false;
          const isValid = Date.now() < Number(expiresAt);
          if (!isValid) {
            localStorage.removeItem("bc-do-not-place-order");
          }
          return isValid;
        },
        getTimeRemaining: () => {
          const expiresAt = localStorage.getItem("bc-do-not-place-order");
          if (!expiresAt) return null;
          const remaining = Number(expiresAt) - Date.now();
          if (remaining <= 0) {
            localStorage.removeItem("bc-do-not-place-order");
            return null;
          }
          return Math.ceil(remaining / 6e4);
        }
      }
    };
    log("Debug helpers available at window.__bcDebug");
    log('Try: __bcDebug.testLDU("RI") or __bcDebug.dryRun.enable()');
  }
}
function disableDebug() {
  debug = false;
  try {
    localStorage == null ? void 0 : localStorage.removeItem(DEBUG_STORAGE_KEY);
  } catch (e) {
  }
  console.info("[externalTracking] Debug mode disabled");
}
function setIdentity(identity) {
  metaPixel.setIdentity(identity);
}
function grantConsent() {
  setConsent(true);
}
function revokeConsent() {
  setConsent(false);
}
function hasConsent() {
  const overrideConsent = localStorage == null ? void 0 : localStorage.getItem(OVERRIDE_CONSENT_KEY);
  if (overrideConsent) {
    return overrideConsent === "true";
  }
  return hasConsent$1();
}
function extractPixelContainerFromCheckoutItems(params, pixelContainer) {
  var _a, _b, _c;
  if ((pixelContainer == null ? void 0 : pixelContainer.brandId) && ((_a = pixelContainer == null ? void 0 : pixelContainer.pixels) == null ? void 0 : _a.length)) {
    return pixelContainer;
  }
  const firstItem = ((_b = params == null ? void 0 : params.checkoutItems) == null ? void 0 : _b[0]) || ((_c = params == null ? void 0 : params.cartItems) == null ? void 0 : _c[0]);
  if ((firstItem == null ? void 0 : firstItem.pixels) && (firstItem == null ? void 0 : firstItem.brandId)) {
    log("Extracted pixel container from first checkout/cart item");
    return {
      brandId: String(firstItem.brandId),
      pixels: firstItem.pixels
    };
  }
  return pixelContainer;
}
function enrichParamsFromCheckoutItems(params) {
  var _a, _b;
  const firstItem = ((_a = params == null ? void 0 : params.checkoutItems) == null ? void 0 : _a[0]) || ((_b = params == null ? void 0 : params.cartItems) == null ? void 0 : _b[0]);
  if (!firstItem) {
    return params;
  }
  return {
    brandId: firstItem.brandId,
    brandName: firstItem.brandName,
    categoryId: firstItem.categoryId,
    categoryName: firstItem.categoryName,
    subCategoryId: firstItem.subCategoryId,
    subCategoryName: firstItem.subCategoryName,
    retailerId: firstItem.retailerId,
    retailerSku: firstItem.retailerSku,
    stacklineSku: firstItem.stacklineSku,
    ...params
    // Original params override defaults
  };
}
function isValidTrackingEvent(eventName, eventPayload) {
  var _a, _b;
  if (!eventName) {
    return false;
  }
  return !!((eventPayload == null ? void 0 : eventPayload.brandId) || (eventPayload == null ? void 0 : eventPayload.stacklineSku) || ((_a = eventPayload == null ? void 0 : eventPayload.checkoutItems) == null ? void 0 : _a.length) > 0 || ((_b = eventPayload == null ? void 0 : eventPayload.cartItems) == null ? void 0 : _b.length) > 0);
}
function checkAndEnableDebugMode(params) {
  if (params == null ? void 0 : params[DEBUG_KEY]) {
    enableDebug();
    delete params[DEBUG_KEY];
    return;
  }
  if (!debug) {
    const debugStored = localStorage == null ? void 0 : localStorage.getItem(DEBUG_STORAGE_KEY);
    if (debugStored === "1") {
      enableDebug();
    }
  }
}
function track(event, params, pixelContainer) {
  var _a, _b, _c, _d;
  checkAndEnableDebugMode(params);
  log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
  log(`Tracking event: ${event}`, "Params:", params, "Pixel Container:", pixelContainer);
  const enrichedPixelContainer = extractPixelContainerFromCheckoutItems(params, pixelContainer);
  let enrichedParams = params;
  if (((_a = params == null ? void 0 : params.checkoutItems) == null ? void 0 : _a.length) === 1 || ((_b = params == null ? void 0 : params.cartItems) == null ? void 0 : _b.length) === 1) {
    enrichedParams = enrichParamsFromCheckoutItems(params);
    log("Enriched params from first checkout/cart item");
  }
  if (!(enrichedParams == null ? void 0 : enrichedParams.v_id)) {
    log("❌ Missing required visitor ID (v_id)");
    return;
  }
  if (!(enrichedPixelContainer == null ? void 0 : enrichedPixelContainer.brandId) || !((_c = enrichedPixelContainer == null ? void 0 : enrichedPixelContainer.pixels) == null ? void 0 : _c.length)) {
    log("❌ Missing brand ID or pixels configuration");
    return;
  }
  if (!hasConsent$1()) {
    log("❌ Tracking blocked: user consent not granted");
    return;
  }
  const pixelsByProvider = enrichedPixelContainer.pixels.reduce(
    (acc, pixel) => {
      acc[pixel.pixelProvider] = [...acc[pixel.pixelProvider] || [], pixel];
      return acc;
    },
    {}
  );
  const shippingState = enrichedParams == null ? void 0 : enrichedParams.shippingState;
  const billingState = enrichedParams == null ? void 0 : enrichedParams.billingState;
  const lduOptions = getLDUOptions(shippingState, billingState);
  const trackingOptions = lduOptions ? { ldu: lduOptions } : null;
  if ((_d = trackingOptions == null ? void 0 : trackingOptions.ldu) == null ? void 0 : _d.enabled) {
    log(`LDU will be applied - shippingState: ${shippingState}, billingState: ${billingState}`);
  }
  const dryRunExpiresAt = localStorage == null ? void 0 : localStorage.getItem("bc-do-not-place-order");
  const isDryRun = dryRunExpiresAt && Date.now() < Number(dryRunExpiresAt);
  Object.entries(pixelsByProvider).forEach(([providerName, providerPixels]) => {
    const provider = trackingProviders[providerName];
    if (!provider) {
      log(`Provider not registered: ${providerName}`);
      return;
    }
    try {
      providerPixels.forEach((pixel) => {
        const { eventName, eventType, eventPayload } = parseEventAndPayload(
          event,
          enrichedParams,
          pixel.eventMapping
        );
        if (!isValidTrackingEvent(eventName, eventPayload) || !eventName) {
          log("Event not supported or missing identifiers:", event);
          return;
        }
        log("✅ Consent granted, event is valid, proceeding with tracking");
        if (isDryRun) {
          console.log(`🧪 [DRY RUN] Would track to ${providerName}:`, {
            eventName,
            eventType,
            eventPayload,
            pixelId: pixel.pixelId,
            trackingOptions
          });
          return;
        }
        provider.track({
          brandId: enrichedPixelContainer.brandId,
          pixels: [pixel],
          eventName,
          eventType,
          eventPayload,
          // Pass tracking options to providers (each extracts what it needs)
          trackingOptions,
          // Pass external tracking reports (each provider extracts its own data)
          externalTracking: enrichedParams == null ? void 0 : enrichedParams.externalTracking
        });
        log(`Tracked with ${providerName}:`, eventName);
      });
    } catch (error) {
      log(`Error tracking with ${providerName}:`, error);
    }
  });
}
export {
  disableDebug,
  enableDebug,
  grantConsent,
  hasConsent,
  registerTrackingProvider,
  revokeConsent,
  setIdentity,
  track
};
//# sourceMappingURL=externalTracking.js.map
